import { useMutation } from '@apollo/client/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Col, Dropdown, Row, message } from 'antd';
import clsx from 'clsx';
import { includes, map, sortBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../../AppContext';
import {
  AddButton,
  DeleteButton,
  DragIcon,
  EditButton,
  InfoIconDark,
  KebabMenu,
  RightIcon,
  WrongIcon,
} from '../../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../../common/CommonUseLazyQuery';
import {
  ADDONS,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  BULK_ACTION,
  BULK_JOB_STATUS,
  DEFAULT_PAGE_SIZE,
  DESELECT_MESSAGE,
  GA_EVENT,
  GA_LABEL,
  PROJECT_CHECKLIST_STATUS,
} from '../../../../../../../../common/constants';
import { Event } from '../../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../../common/useRouter';
import { titleCase } from '../../../../../../../../common/utils';
import AddMultipleModal from '../../../../../../../../components/AddMultipleModal';
import BulkEditStageItemDrawer from '../../../../../../../../components/BulkEditStageItemDrawer';
import CanPerform from '../../../../../../../../components/CanPerform';
import CommonBulkDeleteModal from '../../../../../../../../components/CommonBulkDeleteModal';
import CommonCard from '../../../../../../../../components/CommonCard';
import CommonPopover from '../../../../../../../../components/CommonPopover';
import CommonSelectionBar from '../../../../../../../../components/CommonSelectionBar';
import CommonTable from '../../../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../../../components/CommonTooltip';
import CustomCheckboxSelection from '../../../../../../../../components/CustomCheckboxSelection';
import '../../../../../../../../components/CustomCheckboxSelection.less';
import EllipsisText from '../../../../../../../../components/EllipsisText';
import InfiniteScrollHandler from '../../../../../../../../components/InfiniteScrollHandler';
import Portal from '../../../../../../../../components/Portal';
import {
  BULK_CREATE_PROJECT_EQC_TYPE_STAGE_ITEMS,
  BULK_DELETE_PROJECT_EQC_TYPE_STAGE_ITEMS,
  BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_ITEMS,
  DELETE_PROJECT_STAGE_ITEM,
  REORDER_PROJECT_STAGE_ITEM,
  UPDATE_PROJECT_EQC_STAGE_ITEM,
} from '../../../../../../graphql/Mutation';
import {
  GET_BULK_PROJECT_EQC_TYPE_STAGE_ITEMS_JOB_STATUS,
  GET_PROJECT_STAGE_ITEM_LIST,
} from '../../../../../../graphql/Queries';
import DeleteModalStage from '../DeleteModalStage';
import AddEditModal from './AddEditModal';

const RowContext = React.createContext({});

const DraggableCard = ({ id, children, className }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const [dragHandle, ...restChildren] = React.Children.toArray(
    children?.props?.children,
  );
  return (
    <CommonCard cardRef={setNodeRef} className={className} style={style}>
      <div className="d-flex">
        <div
          className="mr-5 fw-medium drag-handle"
          {...attributes}
          {...listeners}
        >
          {dragHandle}
        </div>
        {restChildren}
      </div>
    </CommonCard>
  );
};
const Rows = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // eslint-disable-next-line react/destructuring-assignment
    id: props['data-row-key'],
  });
  const style = {
    // eslint-disable-next-line react/destructuring-assignment
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};
const DragHandle = ({ disabled }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <DragIcon
      className={clsx(
        'stage-item-drag-handle',
        disabled && 'cursor-not-allowed',
      )}
      ref={setActivatorNodeRef}
      {...(!disabled && listeners)}
    />
  );
};

const StageItemTable = ({
  stageId,
  eqcTypeData,
  isEditable,
  selectedStageData,
  isBulkEnabled,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  isSelectAllAcrossPages,
  setIsSelectAllAcrossPages,
  exceptIds,
  setExceptIds,
  onClearSelection,
  isStageSelectionActive,
}) => {
  const initialStageItemFilter = {
    skip: 0,
    limit: 10,
    sortBy: { field: 'order', order: 'ASC' },
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [showModal, setShowModal] = useState(false);
  const [stageItemData, setStageItemData] = useState();
  const [deleteStageItemModal, setDeleteStageItemModal] = useState(false);
  const [selectedData, setSelectedData] = useState();
  const [stageItemFilter, setStageItemFilter] = useState({
    ...initialStageItemFilter,
    projectEqcTypeStageId: parseInt(stageId, 10),
  });
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [stageItemListData, setStageItemListData] = useState();
  const {
    params: { projectId },
  } = useRouter();
  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const [showAddMultipleModal, setShowAddMultipleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkEditDrawer, setShowBulkEditDrawer] = useState(false);
  const [selectedInputType, setSelectedInputType] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [totalActiveCount, setTotalActiveCount] = useState(0);
  const [newItemCount, setNewItemCount] = useState(0);
  const updateTimeoutRefs = useRef({});
  const bulkJobsRef = useRef(new Map());

  const isAnyRowSelected = selectedRows?.length > 0 || isSelectAllAcrossPages;

  const isPublishedParent = includes(
    [PROJECT_CHECKLIST_STATUS.PUBLISHED, PROJECT_CHECKLIST_STATUS.ARCHIVED],
    eqcTypeData?.projectEqcType?.status,
  );

  useEffect(() => {
    return () => {
      bulkJobsRef.current?.forEach((jobInfo) => {
        if (jobInfo?.timeoutId) {
          clearTimeout(jobInfo.timeoutId);
        }
      });
      bulkJobsRef.current?.clear();
    };
  }, []);

  const startJobTracking = (jobId, action) => {
    if (!jobId) return;
    bulkJobsRef.current?.set(jobId, { timeoutId: null, bulkAction: action });
  };

  const clearJobTracking = (jobId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo?.timeoutId) {
      clearTimeout(jobInfo.timeoutId);
    }
    bulkJobsRef.current?.delete(jobId);
  };

  const updateJobTimeout = (jobId, timeoutId) => {
    if (!jobId) return;
    const jobInfo = bulkJobsRef.current?.get(jobId);
    if (jobInfo) {
      bulkJobsRef.current?.set(jobId, { ...jobInfo, timeoutId });
    }
  };

  const [fetchStageItemData, { loading }] = useLazyQueryWithCallbacks(
    GET_PROJECT_STAGE_ITEM_LIST,
    {
      variables: { filter: stageItemFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.projectEqcTypeStageItemList?.data;
        setTotalActiveCount(
          res?.projectEqcTypeStageItemList?.totalActiveCount || 0,
        );
        setNewItemCount(res?.projectEqcTypeStageItemList?.newItemCount || 0);
        const pagination = {
          ...paginationProp,
          total: res?.projectEqcTypeStageItemList?.total,
        };

        if (scrollFlag) {
          const datalist = [...stageItemListData, ...data];
          setStageItemListData(sortBy(datalist, ['order']));
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setStageItemListData(sortBy(datalist, ['order']));

          // Handle cross-page selection for newly loaded pages
          if (isSelectAllAcrossPages && setSelectedRows && setSelectedRowKeys) {
            const existingIds = selectedRows?.map((row) => row?.id) || [];
            const newItemsToAdd =
              datalist?.filter(
                (item) =>
                  item?.isActive &&
                  !existingIds?.includes(item?.id) &&
                  !exceptIds?.includes(item?.id),
              ) || [];

            const updatedSelectedRows = [
              ...(selectedRows || []),
              ...newItemsToAdd,
            ];
            const updatedSelectedKeys =
              updatedSelectedRows
                ?.map((row) => row?.id)
                ?.filter((id) => !exceptIds?.includes(id)) || [];

            setSelectedRows(updatedSelectedRows);
            setSelectedRowKeys(updatedSelectedKeys);
          }
        }
        setHasMore(!!data?.length);
        setPaginationProp(pagination);
      },
      onError() {},
    },
  );

  const handleRefetchAfterDelete = (deletedCount = 1) => {
    const currentTotal = paginationProp?.total || 0;
    const pageSize = paginationProp?.pageSize || DEFAULT_PAGE_SIZE;
    const newTotal = Math.max(0, currentTotal - deletedCount);
    const maxPage = Math.max(1, Math.ceil(newTotal / pageSize));
    const newCurrent = Math.min(paginationProp?.current || 1, maxPage);
    const newSkip = (newCurrent - 1) * pageSize;

    setStageItemFilter({
      ...stageItemFilter,
      skip: newSkip,
    });
    setPaginationProp({
      ...paginationProp,
      current: newCurrent,
    });
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip: newSkip,
        },
      },
    });
  };

  const [updateProjectEqcTypeStageItem] = useMutation(
    UPDATE_PROJECT_EQC_STAGE_ITEM,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.DELETE_PROJECT_CHECKLIST_STAGE_ITEM, {
          label: GA_LABEL.DELETE_PROJECT_CHECKLIST_STAGE_ITEM,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          project_id: projectId,
          project_stage_id: stageId,
          stage_item_id: selectedData?.id,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setDeleteStageItemModal(false);
        fetchStageItemData({
          variables: { filter: stageItemFilter },
        });
      },
    },
  );

  const [updateStageItemInline] = useMutation(UPDATE_PROJECT_EQC_STAGE_ITEM, {
    onCompleted() {
      fetchStageItemData({
        variables: {
          filter: stageItemFilter,
        },
      });
    },
    onError() {},
  });

  const [deleteStageItem] = useMutation(DELETE_PROJECT_STAGE_ITEM, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.DELETE_PROJECT_CHECKLIST_STAGE_ITEM, {
        label: GA_LABEL.DELETE_PROJECT_CHECKLIST_STAGE_ITEM,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        project_id: projectId,
        project_stage_id: stageId,
        stage_item_id: selectedData?.id,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      setDeleteStageItemModal(false);
      handleRefetchAfterDelete();
    },
  });

  const [reorderStageItem, { loading: reorderLoading }] = useMutation(
    REORDER_PROJECT_STAGE_ITEM,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.REORDER_PROJECT_CHECKLIST_STAGE_ITEM, {
          label: GA_LABEL.REORDER_PROJECT_CHECKLIST_STAGE_ITEM,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          project_id: projectId,
          project_stage_id: stageId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        fetchStageItemData({ variables: { filter: stageItemFilter } });
      },
    },
  );

  const refetchStageDetails = () => {
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          projectEqcTypeStageId: parseInt(stageId, 10),
        },
      },
    });
  };

  const handleClearSelection = () => {
    onClearSelection?.();
    setSelectedInputType(null);
    setBulkAction(null);
  };

  const [getBulkJobStatus] = useLazyQueryWithCallbacks(
    GET_BULK_PROJECT_EQC_TYPE_STAGE_ITEMS_JOB_STATUS,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        const jobId = res?.getBulkProjectEqcTypeStageItemsJobStatus?.jobId;
        const state = res?.getBulkProjectEqcTypeStageItemsJobStatus?.state;

        if (!jobId) return;

        if (state === BULK_JOB_STATUS.PROCESSING) {
          const timeoutId = setTimeout(() => {
            getBulkJobStatus({
              variables: { jobId },
            });
          }, 3000);
          updateJobTimeout(jobId, timeoutId);
        }

        if (
          state === BULK_JOB_STATUS.COMPLETED ||
          state === BULK_JOB_STATUS.FAILED
        ) {
          const jobAction = bulkJobsRef.current?.get(jobId)?.bulkAction;
          clearJobTracking(jobId);

          if (state === BULK_JOB_STATUS.COMPLETED) {
            if (jobAction === BULK_ACTION.ADD_MULTIPLE) {
              refetchStageDetails();
            } else if (jobAction === BULK_ACTION.EDIT) {
              fetchStageItemData({
                variables: {
                  filter: stageItemFilter,
                },
              });
            } else {
              handleRefetchAfterDelete(selectedRowKeys?.length);
            }
          }
          handleClearSelection();
        }
      },
    },
  );

  const [bulkDeleteStageItems, { loading: bulkDeleteLoading }] = useMutation(
    BULK_DELETE_PROJECT_EQC_TYPE_STAGE_ITEMS,
    {
      onCompleted(response) {
        const { status, jobId } =
          response?.bulkDeleteProjectEqcTypeStageItems || {};

        if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
          startJobTracking(jobId, BULK_ACTION.DELETE);
          getBulkJobStatus({
            variables: { jobId },
          });
        }
      },
      onError() {
        onClearSelection?.();
      },
    },
  );

  const handleStageItemUpdate = useCallback(
    (itemId, field, value) => {
      setStageItemListData(
        (prevData) =>
          prevData?.map((item) =>
            item?.id === itemId ? { ...item, [field]: value } : item,
          ) || [],
      );

      if (updateTimeoutRefs.current[itemId]) {
        clearTimeout(updateTimeoutRefs.current[itemId]);
      }

      updateTimeoutRefs.current[itemId] = setTimeout(() => {
        updateStageItemInline({
          variables: {
            id: itemId,
            data: {
              [field]: value,
              projectEqcTypeStageId: parseInt(stageId, 10),
            },
          },
        });
        delete updateTimeoutRefs.current[itemId];
      }, 400);
    },
    [updateStageItemInline],
  );

  const handleBulkDelete = async () => {
    if (!selectedRowKeys?.length && !isSelectAllAcrossPages) {
      message.warning('Please select items to delete');
      return;
    }

    try {
      const mutationData = {
        isAllSelected: isSelectAllAcrossPages,
        projectEqcTypeStageId: Number(stageId),
      };

      if (!isSelectAllAcrossPages) {
        mutationData.projectEqcTypeStageItemIds =
          selectedRows?.map((row) => row?.id) || [];
      }
      if (isSelectAllAcrossPages && exceptIds?.length > 0) {
        mutationData.exceptProjectEqcTypeStageItemIds = exceptIds;
      }

      await bulkDeleteStageItems({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      return error;
    }
  };

  // Auto-clear selection when all items have been excluded
  useEffect(() => {
    if (
      isSelectAllAcrossPages &&
      exceptIds?.length >= (paginationProp?.total || 0) &&
      paginationProp?.total > 0
    ) {
      handleClearSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptIds, isSelectAllAcrossPages, paginationProp?.total]);

  // Restore selections when data changes - show which items on current page are selected
  useEffect(() => {
    if (
      selectedRows?.length > 0 &&
      stageItemListData?.length > 0 &&
      setSelectedRowKeys
    ) {
      const currentPageIds = stageItemListData?.map((item) => item?.id) || [];
      const selectedIdsOnCurrentPage =
        selectedRows
          ?.filter(
            (row) =>
              currentPageIds?.includes(row?.id) &&
              !(isSelectAllAcrossPages && exceptIds?.includes(row?.id)),
          )
          ?.map((row) => row?.id) || [];

      setSelectedRowKeys(selectedIdsOnCurrentPage);
    } else if (
      selectedRows?.length === 0 &&
      stageItemListData?.length > 0 &&
      setSelectedRowKeys
    ) {
      setSelectedRowKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageItemListData, selectedRows, setSelectedRowKeys]);

  // Cleanup updateTimeoutRefs on unmount
  useEffect(() => {
    return () => {
      Object.values(updateTimeoutRefs.current).forEach((timeout) => {
        if (timeout) {
          clearTimeout(timeout);
        }
      });
      updateTimeoutRefs.current = {};
    };
  }, []);

  useEffect(() => {
    if (stageId) {
      setStageItemFilter({
        ...initialStageItemFilter,
        projectEqcTypeStageId: parseInt(stageId, 10),
      });
      setPaginationProp(initialPaginationValue);
      fetchStageItemData({
        variables: {
          filter: {
            ...initialStageItemFilter,
            projectEqcTypeStageId: parseInt(stageId, 10),
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId]);

  const handleAddEdit = (record) => {
    if (record) {
      setStageItemData(record);
    } else {
      setStageItemData();
    }
    setShowModal(true);
  };

  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setStageItemFilter({
      ...stageItemFilter,
      skip,
      limit: pagination.pageSize,
    });
    setPaginationProp({ ...paginationProp, ...pagination });
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip,
          limit: pagination.pageSize,
        },
      },
    });
  };

  const handleActiveAndDelete = (record) => {
    setDeleteStageItemModal(true);
    setSelectedData(record);
  };

  const getChangeLogInfo = (record) => {
    const logKeys = ['description', 'photo', 'isActive', 'remark'];
    const changeData = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const element in record?.previousHistory) {
      if (
        includes(logKeys, element) &&
        record[element] !== record?.previousHistory[element]
      ) {
        const messageData = {};
        if (element === 'photo' || element === 'remark') {
          messageData.message = `${titleCase(element)} requirement is turned ${
            record[element] ? 'on' : 'off'
          }`;
        } else if (element === 'isActive') {
          messageData.message = `Checkpoint is ${
            record[element] ? 'activated' : 'deactivated'
          }`;
        } else {
          messageData.message = `Item ${element} updated`;
        }
        changeData.push(messageData);
      }
    }
    if (!record?.projectEqcTypeStageItemId) {
      const msg = `New checkpoint added`;
      changeData.push({ message: msg });
    }

    return (
      includes(
        [PROJECT_CHECKLIST_STATUS.PUBLISHED, PROJECT_CHECKLIST_STATUS.ARCHIVED],
        eqcTypeData?.projectEqcType?.status,
      ) &&
      changeData?.length > 0 && (
        <CommonPopover
          classNames={{
            root: 'checklist-changelog-popup',
            container: 'checklist-approvers-container',
          }}
          title="Changes made to the main file"
          trigger={isDesktopViewport ? 'hover' : 'click'}
          placement={isDesktopViewport ? 'right' : 'top'}
          destroyOnHidden
          // eslint-disable-next-line no-undef
          getPopupContainer={() => document.body}
          content={
            <div onClick={(e) => e.stopPropagation()}>
              <ul>
                {map(changeData, (data) => {
                  return <li>{data?.message}</li>;
                })}
              </ul>
            </div>
          }
        >
          <InfoIconDark
            width={20}
            height={20}
            className={clsx(isDesktopViewport ? 'mr-20' : 'mr-5', 'info-icon')}
          />
        </CommonPopover>
      )
    );
  };

  const getActionButtons = (record, index) => {
    return (
      <CanPerform
        action={ALLOWED_ACTION_KEYS.EDIT_PROJECT_CHECKLISTS}
        type={ALLOWED_ACTION_TYPE.BOTH}
      >
        {isEditable ? (
          <div className={clsx(isDesktopViewport && 'd-flex', 'action-icons')}>
            {getChangeLogInfo(record)}
            <CommonTooltip
              title={
                isAnyRowSelected
                  ? DESELECT_MESSAGE
                  : (eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.UNPUBLISHED ||
                      !eqcTypeData?.project?.isActive) &&
                    'Edit'
              }
              trigger="focus"
            >
              <Button
                shape="circle"
                icon={<EditButton />}
                className={clsx(
                  'edit-button pointer b-0',
                  (isAnyRowSelected ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                    !eqcTypeData?.project?.isActive) &&
                    'disabled-button',
                )}
                disabled={!record?.isActive || isAnyRowSelected}
                onClick={() => {
                  handleAddEdit(record);
                }}
                id={`stage-item-edit-button-${index}`}
              />
            </CommonTooltip>
            {!includes(
              [
                PROJECT_CHECKLIST_STATUS.PUBLISHED,
                PROJECT_CHECKLIST_STATUS.ARCHIVED,
              ],
              eqcTypeData?.projectEqcType?.status,
            ) && (
              <CommonTooltip
                title={
                  isAnyRowSelected
                    ? DESELECT_MESSAGE
                    : (eqcTypeData?.status ===
                        PROJECT_CHECKLIST_STATUS?.UNPUBLISHED ||
                        !eqcTypeData?.project?.isActive) &&
                      'Delete'
                }
                trigger="focus"
              >
                <Button
                  className={clsx(
                    'delete-button pointer b-0',
                    (isAnyRowSelected ||
                      eqcTypeData?.status ===
                        PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                      eqcTypeData?.status ===
                        PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                      !eqcTypeData?.project?.isActive) &&
                      'disabled-button',
                  )}
                  shape="circle"
                  icon={<DeleteButton />}
                  disabled={
                    isAnyRowSelected ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                    !eqcTypeData?.project?.isActive
                  }
                  onClick={() => {
                    handleActiveAndDelete(record);
                  }}
                  id={`stage-item-delete-button-${index}`}
                />
              </CommonTooltip>
            )}
            {includes(
              [
                PROJECT_CHECKLIST_STATUS.PUBLISHED,
                PROJECT_CHECKLIST_STATUS.ARCHIVED,
              ],
              eqcTypeData?.projectEqcType?.status,
            ) && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: record?.isActive ? 'deactivate' : 'activate',
                      label: (
                        <span onClick={() => handleActiveAndDelete(record)}>
                          {' '}
                          {record?.isActive ? 'Deactivate' : 'Activate'}{' '}
                        </span>
                      ),
                    },
                  ],
                }}
                getPopupContainer={(triggerNode) => triggerNode?.parentNode}
                trigger={['click']}
              >
                <CommonTooltip
                  trigger="hover"
                  title={
                    isAnyRowSelected || isStageSelectionActive
                      ? DESELECT_MESSAGE
                      : ''
                  }
                >
                  <Button
                    shape="circle"
                    className="kebab-icon"
                    icon={<KebabMenu />}
                    disabled={isAnyRowSelected || isStageSelectionActive}
                    onClick={(e) => e.stopPropagation()}
                  />
                </CommonTooltip>
              </Dropdown>
            )}
          </div>
        ) : (
          <div className="d-flex justify-end">
            <div
              className="pointer text-underline mr-10"
              onClick={() => {
                handleAddEdit(record);
              }}
              id={`stage-item-view-link-${index}`}
            >
              View
            </div>
          </div>
        )}
      </CanPerform>
    );
  };

  const columns = [
    ...(isEditable
      ? [
          {
            width: '5%',
            className: 'drag-visible',
            render: () =>
              eqcTypeData?.project?.isActive && (
                <CommonTooltip
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.ant-table-content')
                  }
                  title="Change Order"
                >
                  <div id="stage-item-drag-handle">
                    <DragHandle disabled={isAnyRowSelected} />
                  </div>
                </CommonTooltip>
              ),
          },
        ]
      : []),
    {
      title: 'ID',
      key: 'id',
      render: (_, __, index) => {
        return <div>{stageItemFilter?.skip + index + 1}</div>;
      },
    },
    {
      title: 'CHECKPOINT',
      dataIndex: 'title',
      key: 'title',
      render: (record) => (
        <div>
          <EllipsisText text={record} />
        </div>
      ),
    },
    {
      title: 'INPUT',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => {
        const isDisabled = isAnyRowSelected || !record?.isActive;
        return (
          <div
            className={isDisabled ? 'cursor-not-allowed' : 'pointer'}
            onClick={() => !isDisabled && handleAddEdit(record)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: 'PHOTOS',
      key: 'photo',
      render: (record) => {
        const isPhoto = record?.photo === true;
        const canToggle =
          isBulkEnabled && !isAnyRowSelected && record?.isActive;
        return (
          <div
            className={clsx(
              'd-flex align-center',
              canToggle && 'stage-item-toggle-icon',
              (isAnyRowSelected || !record?.isActive) && 'cursor-not-allowed',
            )}
            onClick={() =>
              canToggle && handleStageItemUpdate(record?.id, 'photo', !isPhoto)
            }
          >
            {isPhoto ? (
              <RightIcon
                className={clsx(
                  canToggle && 'stage-item-toggle-icon success-state',
                )}
              />
            ) : (
              <WrongIcon
                className={clsx(
                  'wrong-icon',
                  canToggle && 'stage-item-toggle-icon error-state',
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'REMARKS',
      key: 'remark',
      render: (record) => {
        const isRemark = record?.remark === true;
        const canToggle =
          isBulkEnabled && !isAnyRowSelected && record?.isActive;
        return (
          <div
            className={clsx(
              'd-flex align-center',
              canToggle && 'stage-item-toggle-icon',
              (isAnyRowSelected || !record?.isActive) && 'cursor-not-allowed',
            )}
            onClick={() =>
              canToggle &&
              handleStageItemUpdate(record?.id, 'remark', !isRemark)
            }
          >
            {isRemark ? (
              <RightIcon
                className={clsx(
                  canToggle && 'stage-item-toggle-icon success-state',
                )}
              />
            ) : (
              <WrongIcon
                className={clsx(
                  'wrong-icon',
                  canToggle && 'stage-item-toggle-icon error-state',
                )}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'action',
      render: (record, _, index) => getActionButtons(record, index),
    },
  ];

  const DraggableBodyRow = async ({ over, active }) => {
    if (!active || !over || active?.id === over?.id) return;
    if (isAnyRowSelected) return;
    const overIndex = over?.data?.current?.sortable?.index;
    const activeIndex = active?.data?.current?.sortable?.index;
    const data = [...stageItemListData];
    setStageItemListData(arrayMove(data, activeIndex, overIndex));
    try {
      const { errors } = await reorderStageItem({
        variables: {
          data: {
            stageItemIds: [active?.id, over?.id],
            projectEqcTypeStageId: parseInt(stageId, 10),
          },
        },
      });
      if (errors) {
        throw errors;
      }
    } catch (error) {
      setStageItemListData(data);
    }
  };

  const handleRefetch = () => {
    fetchStageItemData({
      variables: {
        filter: {
          ...stageItemFilter,
          skip: stageItemListData?.length,
          limit: DEFAULT_PAGE_SIZE,
        },
      },
    });
  };

  // Selection handlers
  const handleRowSelectionChange = (selectedKeys, selectedRowsData) => {
    if (!setSelectedRows || !setSelectedRowKeys) {
      setSelectedRowKeys?.(selectedKeys);
      return;
    }

    if (isSelectAllAcrossPages) {
      const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];
      const newlyDeselected =
        currentPageKeys?.filter((key) => !selectedKeys?.includes(key)) || [];
      const reSelected =
        selectedKeys?.filter((key) => exceptIds?.includes(key)) || [];
      setExceptIds?.((prev) => {
        let updated = [...prev];
        newlyDeselected?.forEach((id) => {
          if (!updated?.includes(id)) updated.push(id);
        });
        updated = updated?.filter((id) => !reSelected?.includes(id));
        return updated;
      });
      setSelectedRowKeys?.(selectedKeys);
      return;
    }

    const currentPageKeys = stageItemListData?.map((item) => item?.id) || [];

    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !currentPageKeys?.includes(row?.id)) || [];

    const activeSelectedRowsData =
      selectedRowsData?.filter((row) => row?.isActive) || [];
    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...activeSelectedRowsData,
    ];

    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectRow = (record, selected) => {
    if (!setSelectedRows || !setSelectedRowKeys) return;
    if (!record?.isActive) return;

    const recordId = record?.id;

    if (selected) {
      if (isSelectAllAcrossPages) {
        setExceptIds?.((prev) => prev?.filter((id) => id !== recordId) || []);
        setSelectedRowKeys?.((prev) => [...prev, recordId]);
        return;
      }
      const isAlreadySelected = selectedRows?.some(
        (row) => row?.id === recordId,
      );
      const newSelectedRows = isAlreadySelected
        ? selectedRows
        : [...(selectedRows || []), record];
      const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];

      setSelectedRows(newSelectedRows);
      setSelectedRowKeys(newSelectedKeys);
      return;
    }

    if (isSelectAllAcrossPages) {
      setExceptIds?.((prev) => [...prev, recordId]);
      const remainingKeys =
        selectedRowKeys?.filter((key) => key !== recordId) || [];
      setSelectedRowKeys?.(remainingKeys);
      return;
    }

    const newSelectedRows =
      selectedRows?.filter((row) => row?.id !== recordId) || [];
    const newSelectedKeys = newSelectedRows?.map((row) => row?.id) || [];
    setSelectedRows(newSelectedRows);
    setSelectedRowKeys(newSelectedKeys);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      const activeItems =
        stageItemListData?.filter((item) => item?.isActive) || [];
      if (isSelectAllAcrossPages) {
        const activePageKeys = activeItems?.map((item) => item?.id) || [];
        setExceptIds?.(
          (prev) => prev?.filter((id) => !activePageKeys?.includes(id)) || [],
        );
        setSelectedRowKeys?.(activePageKeys);
        return;
      }
      const allKeys = activeItems?.map((item) => item?.id) || [];
      setSelectedRowKeys?.(allKeys);
      setSelectedRows?.(activeItems);
    } else {
      if (isSelectAllAcrossPages) {
        const currentPageKeys =
          stageItemListData?.map((item) => item?.id) || [];
        setExceptIds?.((prev) => {
          const updated = [...prev];
          currentPageKeys?.forEach((id) => {
            if (!updated?.includes(id)) updated.push(id);
          });
          return updated;
        });
        setSelectedRowKeys?.([]);
        return;
      }
      setSelectedRowKeys?.([]);
      setSelectedRows?.([]);
    }
  };

  const handleSelectAllOnPage = () => {
    if (!setSelectedRows || !setSelectedRowKeys) return;

    const activeItems =
      stageItemListData?.filter((item) => item?.isActive) || [];
    const activePageKeys = activeItems?.map((item) => item?.id) || [];
    setIsSelectAllAcrossPages?.(false);
    setExceptIds?.([]);

    const existingRowsWithoutCurrentPage =
      selectedRows?.filter((row) => !activePageKeys?.includes(row?.id)) || [];

    const updatedSelectedRows = [
      ...existingRowsWithoutCurrentPage,
      ...activeItems,
    ];

    const updatedSelectedKeys =
      updatedSelectedRows?.map((row) => row?.id) || [];

    setSelectedRows(updatedSelectedRows);
    setSelectedRowKeys(updatedSelectedKeys);
  };

  const handleSelectAllAcrossPages = () => {
    if (!setIsSelectAllAcrossPages) return;

    try {
      setExceptIds?.([]);
      setIsSelectAllAcrossPages(true);

      const activePageKeys =
        stageItemListData
          ?.filter((item) => item?.isActive)
          ?.map((item) => item?.id) || [];
      setSelectedRowKeys?.(activePageKeys);
    } catch (error) {
      message.error('Failed to select all items across pages');
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleRowSelectionChange,
    onSelect: handleSelectRow,
    onSelectAll: handleSelectAll,
    getCheckboxProps: (record) => ({
      disabled: !record?.isActive,
      name: record?.title,
    }),
    renderCell: (checked, record, index, originNode) => {
      if (!record?.isActive) {
        return (
          <CommonTooltip
            title="You cannot select the deactivated checkpoints"
            getPopupContainer={
              () =>
                // eslint-disable-next-line no-undef
                document.getElementById('project-stage-item-module') ||
                document.body // eslint-disable-line no-undef
            }
          >
            {originNode}
          </CommonTooltip>
        );
      }
      return originNode;
    },
    columnTitle: (
      <CustomCheckboxSelection
        checked={
          selectedRowKeys?.length === stageItemListData?.length &&
          stageItemListData?.length > 0
        }
        indeterminate={
          selectedRowKeys?.length > 0 &&
          selectedRowKeys?.length < stageItemListData?.length
        }
        disabled={!stageItemListData?.length}
        onSelectAllPage={handleSelectAllOnPage}
        onSelectAllAcross={handleSelectAllAcrossPages}
        onDeselectAll={handleClearSelection}
        currentPageCount={stageItemListData?.length}
        totalCount={paginationProp?.total}
        isSelectAllAcrossPages={isSelectAllAcrossPages}
        selectedCount={
          isSelectAllAcrossPages
            ? (totalActiveCount || 0) - (exceptIds?.length || 0)
            : selectedRowKeys?.length || 0
        }
      />
    ),
  };

  return (
    <div id="project-stage-item-module">
      {showModal && (
        <AddEditModal
          showModal={showModal}
          setShowModal={setShowModal}
          stageData={stageItemData}
          selectedStageData={selectedStageData}
          isUpdate={!!stageItemData}
          setStageData={setStageItemData}
          refetchStageDataWithInitialValues={refetchStageDetails}
          stageId={stageId}
          eqcTypeData={eqcTypeData}
          isDisabled={
            eqcTypeData?.status === PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
            eqcTypeData?.status === PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
            !eqcTypeData?.project?.isActive
          }
          isEditable={isEditable}
        />
      )}
      {deleteStageItemModal && (
        <DeleteModalStage
          showModal={deleteStageItemModal}
          setShowModal={setDeleteStageItemModal}
          data={selectedData}
          mutation={
            !includes(
              [
                PROJECT_CHECKLIST_STATUS.PUBLISHED,
                PROJECT_CHECKLIST_STATUS.ARCHIVED,
              ],
              eqcTypeData?.projectEqcType?.status,
            )
              ? deleteStageItem
              : updateProjectEqcTypeStageItem
          }
          stageId={stageId}
          isDeletable={
            !includes(
              [
                PROJECT_CHECKLIST_STATUS.PUBLISHED,
                PROJECT_CHECKLIST_STATUS.ARCHIVED,
              ],
              eqcTypeData?.projectEqcType?.status,
            )
          }
        />
      )}
      {eqcTypeData?.project?.isActive && isEditable && (
        <CanPerform
          action={ALLOWED_ACTION_KEYS.ADD_PROJECT_STAGE_ITEM}
          type={ALLOWED_ACTION_TYPE.BOTH}
        >
          <Portal portalId="stageitem-add-btn">
            {isBulkEnabled ? (
              <div className="d-flex">
                <CommonTooltip
                  title={
                    isStageSelectionActive || isAnyRowSelected
                      ? DESELECT_MESSAGE
                      : ''
                  }
                >
                  <Button
                    shape="round"
                    type="primary"
                    id="add-btn"
                    icon={<AddButton />}
                    className="add-button"
                    disabled={isAnyRowSelected || isStageSelectionActive}
                    onClick={() => {
                      handleAddEdit();
                    }}
                  >
                    {isDesktopViewport && 'Item'}
                  </Button>
                </CommonTooltip>
                <CommonTooltip
                  title={
                    isStageSelectionActive || isAnyRowSelected
                      ? DESELECT_MESSAGE
                      : ''
                  }
                >
                  <Button
                    shape="round"
                    type="default"
                    id="add-bulk-btn"
                    className="add-multiple-button"
                    icon={<AddButton />}
                    disabled={isAnyRowSelected || isStageSelectionActive}
                    onClick={() => {
                      setShowAddMultipleModal(true);
                      setBulkAction(BULK_ACTION.ADD_MULTIPLE);
                    }}
                  >
                    {isDesktopViewport && 'Bulk Items'}
                  </Button>
                </CommonTooltip>
              </div>
            ) : (
              <CommonTooltip
                title={isStageSelectionActive ? DESELECT_MESSAGE : ''}
              >
                <Button
                  shape="round"
                  type="primary"
                  id="add-btn"
                  icon={<AddButton />}
                  className="add-button"
                  disabled={
                    isStageSelectionActive ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.PUBLISHED ||
                    eqcTypeData?.status ===
                      PROJECT_CHECKLIST_STATUS?.ARCHIVED ||
                    !eqcTypeData?.project?.isActive
                  }
                  onClick={() => {
                    handleAddEdit();
                  }}
                >
                  Add
                </Button>
              </CommonTooltip>
            )}
          </Portal>
        </CanPerform>
      )}
      <Portal portalId="stage-item-selection-bar">
        {isDesktopViewport && isAnyRowSelected && (
          <CommonSelectionBar
            selectedCount={
              isSelectAllAcrossPages
                ? (totalActiveCount || 0) - (exceptIds?.length || 0)
                : selectedRows?.length || 0
            }
            className="ml-10 mr-10"
            onClear={handleClearSelection}
          >
            <CommonTooltip trigger="hover" title="Bulk Edit">
              <Button
                shape="round"
                icon={<EditButton />}
                className="edit-button pointer b-0"
                onClick={() => {
                  setShowBulkEditDrawer(true);
                  setBulkAction(BULK_ACTION.EDIT);
                }}
              />
            </CommonTooltip>
            <CommonTooltip trigger="hover" title="Bulk Delete">
              <Button
                className="delete-button pointer b-0 ml-5 mr-5"
                shape="round"
                icon={<DeleteButton />}
                onClick={() => {
                  setShowDeleteModal(true);
                  setBulkAction(BULK_ACTION.DELETE);
                }}
              />
            </CommonTooltip>
          </CommonSelectionBar>
        )}
      </Portal>
      {selectedStageData?.type === ADDONS.RFI && (
        <Alert
          title={
            <>
              This is a view-only tab. System admin or Project admin can make
              changes by clicking on the Project <b>Settings</b> button and
              click
              <b> RFI</b>.
            </>
          }
          type="info"
          showIcon
          className="mb-10 font-size-12"
        />
      )}
      <DndContext
        onDragEnd={DraggableBodyRow}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        id="stage-item-drag-context"
      >
        {isDesktopViewport ? (
          <SortableContext
            items={stageItemListData?.map((i) => i?.id) ?? []}
            strategy={verticalListSortingStrategy}
            id="stage-item-desktop-sortable-context"
          >
            <CommonTable
              components={{ body: { row: Rows } }}
              columns={columns}
              dataSource={stageItemListData || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowKey="id"
              loading={loading || reorderLoading || bulkDeleteLoading}
              rowSelection={
                isBulkEnabled && isDesktopViewport ? rowSelection : undefined
              }
              rowClassName={(record) => {
                const classes = [];
                if (!record?.isActive) classes.push('row-disabled');
                if (isAnyRowSelected) {
                  classes.push('cursor-not-allowed');
                }
                return classes.join(' ');
              }}
              onRow={(_, index) => {
                return {
                  id: `stage-item-row-${index}`,
                };
              }}
              id="stage-item-table"
            />
          </SortableContext>
        ) : (
          <InfiniteScrollHandler
            scrollFlag={scrollFlag}
            loading={loading || reorderLoading}
            refetchData={handleRefetch}
            setScrollFlag={setScrollFlag}
            hasMore={hasMore}
            wrapperClassName="stage-item-scroll-wrapper"
            dataLength={stageItemListData?.length}
            skeletonRows={columns?.length - 3}
            id="stage-item-infinite-scroll"
          >
            <SortableContext
              items={stageItemListData}
              disabled={!isEditable}
              strategy={rectSortingStrategy}
              id="stage-item-mobile-sortable-context"
            >
              {map(stageItemListData, (item, index) => {
                return (
                  <DraggableCard
                    id={item?.id}
                    className="stage-card"
                    key={item?.id}
                  >
                    <div className="common-card d-flex">
                      {isEditable && eqcTypeData?.project?.isActive && (
                        <DragHandle />
                      )}
                      <div>
                        <div className="card-header fw-medium">
                          <Row wrap={false}>
                            <Col flex="auto" className="d-flex">
                              <span className="mr-5">{index + 1}.</span>
                              <EllipsisText text={item?.title} />
                            </Col>
                          </Row>
                        </div>
                        <div className="card-content text-secondary">
                          <br />
                          <div className="mb-15">
                            <span className="fw-medium mr-5">Input:</span>
                            {item?.type}
                          </div>
                          <div className="mb-15 d-flex align-center">
                            <span className="fw-medium mr-5">Photos:</span>
                            {item?.photo === true ? (
                              <RightIcon />
                            ) : (
                              <WrongIcon className="wrong-icon" />
                            )}
                          </div>
                          <div className=" d-flex align-center">
                            <span className="fw-medium mr-5">Remarks:</span>
                            {item?.remark === true ? (
                              <RightIcon />
                            ) : (
                              <WrongIcon className="wrong-icon" />
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="d-flex position-absolute stage-item-action-btn">
                        {getActionButtons(item, index)}
                      </span>
                    </div>
                  </DraggableCard>
                );
              })}
            </SortableContext>
          </InfiniteScrollHandler>
        )}
      </DndContext>
      {showAddMultipleModal && (
        <AddMultipleModal
          showModal={showAddMultipleModal}
          setShowModal={setShowAddMultipleModal}
          stageId={stageId}
          stageIdFieldName="projectEqcTypeStageId"
          refetchStageData={refetchStageDetails}
          mutation={BULK_CREATE_PROJECT_EQC_TYPE_STAGE_ITEMS}
          mutationResponseField="bulkCreateProjectEqcTypeStageItems"
          analyticsEvent={GA_EVENT.BULK_ADD_PROJECT_CHECKLIST_STAGE_ITEMS}
          analyticsLabel={GA_LABEL.BULK_ADD_PROJECT_CHECKLIST_STAGE_ITEMS}
          analyticsContextId={projectId}
          analyticsContextIdField="project_id"
          modalTitle="Add Multiple Checkpoints"
          onJobStarted={(jobId) => {
            if (jobId) {
              startJobTracking(jobId, BULK_ACTION.ADD_MULTIPLE);
              getBulkJobStatus({
                variables: { jobId },
              });
            }
          }}
        />
      )}
      {showBulkEditDrawer && (
        <BulkEditStageItemDrawer
          showDrawer={showBulkEditDrawer}
          setShowDrawer={setShowBulkEditDrawer}
          selectedInputType={selectedInputType}
          setSelectedInputType={setSelectedInputType}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          exceptIds={exceptIds}
          paginationProp={paginationProp}
          selectedRows={selectedRows}
          startJobTracking={startJobTracking}
          getBulkJobStatus={getBulkJobStatus}
          bulkAction={bulkAction}
          stageId={stageId}
          mutation={BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_ITEMS}
          mutationResponseField="bulkUpdateProjectEqcTypeStageItems"
          stageIdFieldName="projectEqcTypeStageId"
          itemIdsFieldName="projectEqcTypeStageItemIds"
          exceptIdsFieldName="exceptProjectEqcTypeStageItemIds"
          containerId="project-stage-item-module"
          isPublishedParent={isPublishedParent}
          newItemCount={newItemCount}
        />
      )}
      {showDeleteModal && (
        <CommonBulkDeleteModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          selectedCount={
            isSelectAllAcrossPages
              ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
              : selectedRows?.length || 0
          }
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          totalCount={paginationProp?.total || 0}
          onConfirm={handleBulkDelete}
          loading={bulkDeleteLoading}
          entityName="checkpoint"
          entityNamePlural="checkpoints"
        />
      )}
    </div>
  );
};

export default StageItemTable;
